import React from 'react';
import { motion, MotionConfig, Variants } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const stagger: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09, delayChildren: 0.04 } },
};

const inView = {
  initial: 'hidden' as const,
  whileInView: 'show' as const,
  viewport: { once: true, margin: '-80px' },
};

const About: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative isolate min-h-screen w-full bg-white">
        <div aria-hidden className="blueprint-grid pointer-events-none absolute inset-0 z-0" />

        <div className="relative z-10">
          {/* ── Hero ── */}
          <section className="mx-auto max-w-4xl px-6 pt-20 pb-16 text-center sm:pt-28">
            <motion.div initial="hidden" animate="show" variants={stagger}>
              <motion.span variants={fadeUp} className="eyebrow">
                About Steelman
              </motion.span>
              <motion.h1
                variants={fadeUp}
                className="mt-5 text-balance font-serif text-5xl leading-[1.05] tracking-tight text-charcoal sm:text-6xl"
              >
                Discourse deserves a better arena.
              </motion.h1>
              <motion.p
                variants={fadeUp}
                className="mx-auto mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-ink-muted sm:text-xl"
              >
                We built Steelman to elevate how people engage with ideas — by arguing
                the best version of every position, not the weakest.
              </motion.p>
            </motion.div>
          </section>

          {/* ── Definition block (dictionary entry) ── */}
          <section className="mx-auto max-w-3xl px-6 pb-20">
            <motion.div
              {...inView}
              variants={fadeUp}
              className="card-frame relative overflow-hidden p-8 sm:p-10"
            >
              <div className="flex items-baseline gap-3">
                <h2 className="font-serif text-4xl text-charcoal">steelman</h2>
                <span className="font-mono text-sm italic text-ink-muted">verb</span>
              </div>
              <p className="mt-1 font-mono text-xs text-ink-muted">/ˈstiːl·man/ · opposite of strawman</p>
              <div className="mt-6 space-y-4 border-t border-hairline pt-6">
                <p className="text-pretty text-base leading-relaxed text-charcoal/85">
                  <span className="mr-3 font-mono text-xs text-ink-muted">1.</span>
                  To strengthen an opposing argument to its best possible form before
                  engaging with it.
                </p>
                <p className="text-pretty text-base leading-relaxed text-charcoal/85">
                  <span className="mr-3 font-mono text-xs text-ink-muted">2.</span>
                  The intellectual practice of debating the strongest version of your
                  opponent's position — leading to better thinking, deeper
                  understanding, and more productive discourse.
                </p>
              </div>
            </motion.div>
          </section>

          {/* ── Strawman vs Steelman ── */}
          <section className="border-t border-black/[0.07]">
            <div className="mx-auto max-w-5xl px-6 py-20">
              <motion.div {...inView} variants={fadeUp} className="mx-auto max-w-2xl text-center">
                <span className="eyebrow">The difference</span>
                <h2 className="mt-4 text-balance font-serif text-3xl tracking-tight text-charcoal sm:text-4xl">
                  Two ways to meet an argument
                </h2>
              </motion.div>

              <motion.div
                {...inView}
                variants={stagger}
                className="mt-12 grid grid-cols-1 overflow-hidden rounded-2xl border border-black/[0.08] bg-white/85 backdrop-blur-sm sm:grid-cols-2 sm:divide-x sm:divide-black/[0.08]"
              >
                <motion.div variants={fadeUp} className="border-b border-black/[0.08] p-8 sm:border-b-0 sm:p-10">
                  <span className="font-mono text-xs uppercase tracking-eyebrow text-ink-muted/70">
                    Strawman
                  </span>
                  <p className="mt-4 font-serif text-2xl leading-snug text-charcoal/50">
                    "Attack the weakest, most distorted version of the other side — and
                    declare victory."
                  </p>
                  <p className="mt-4 text-pretty text-[15px] leading-relaxed text-ink-muted">
                    Easy, satisfying, and everywhere — and it teaches you nothing.
                  </p>
                </motion.div>
                <motion.div variants={fadeUp} className="bg-charcoal p-8 sm:p-10">
                  <span className="font-mono text-xs uppercase tracking-eyebrow text-white/50">
                    Steelman
                  </span>
                  <p className="mt-4 font-serif text-2xl leading-snug text-white">
                    "Build the strongest case the other side could make — then answer
                    it."
                  </p>
                  <p className="mt-4 text-pretty text-[15px] leading-relaxed text-white/60">
                    Harder, rarer, and the only way arguments actually improve.
                  </p>
                </motion.div>
              </motion.div>
            </div>
          </section>

          {/* ── Core values ── */}
          <section className="border-t border-black/[0.07]">
            <div className="mx-auto max-w-6xl px-6 py-20">
              <motion.div {...inView} variants={fadeUp} className="mx-auto max-w-2xl text-center">
                <span className="eyebrow">Our principles</span>
                <h2 className="mt-4 text-balance font-serif text-3xl tracking-tight text-charcoal sm:text-4xl">
                  What we hold ourselves to
                </h2>
              </motion.div>

              <motion.div
                {...inView}
                variants={stagger}
                className="mt-12 grid grid-cols-1 overflow-hidden rounded-2xl border border-black/[0.08] bg-white/80 backdrop-blur-sm md:grid-cols-3 md:divide-x md:divide-black/[0.08]"
              >
                {[
                  {
                    n: '01',
                    title: 'Intellectual integrity',
                    description:
                      'We engage with the strongest versions of all arguments, never convenient caricatures.',
                  },
                  {
                    n: '02',
                    title: 'Multiple perspectives',
                    description:
                      'Complex issues deserve rigorous exploration from all angles, free from bias.',
                  },
                  {
                    n: '03',
                    title: 'Clarity over noise',
                    description:
                      'AI-generated arguments cut through outrage and emotion to what actually matters.',
                  },
                ].map((value) => (
                  <motion.div
                    key={value.n}
                    variants={fadeUp}
                    className="group border-b border-black/[0.08] p-8 transition-colors duration-300 last:border-b-0 hover:bg-white md:border-b-0"
                  >
                    <span className="font-mono text-xs text-ink-muted/60">{value.n}</span>
                    <h3 className="mt-5 font-serif text-2xl text-charcoal">{value.title}</h3>
                    <p className="mt-3 text-pretty text-[15px] leading-relaxed text-ink-muted">
                      {value.description}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* ── Mission ── */}
          <section className="border-t border-black/[0.07]">
            <div className="mx-auto max-w-3xl px-6 py-20 text-center">
              <motion.div {...inView} variants={fadeUp}>
                <span className="eyebrow">Our mission</span>
                <p className="mt-6 text-balance font-serif text-3xl leading-snug text-charcoal sm:text-4xl">
                  The best ideas emerge from rigorous debate and{' '}
                  <em className="text-charcoal/70">mutual understanding</em>.
                </p>
                <p className="mx-auto mt-6 max-w-xl text-pretty text-lg leading-relaxed text-ink-muted">
                  Whether you're weighing a personal decision, researching a complex
                  issue, or just curious what the other side really thinks, Steelman is
                  a space for intellectual exploration and growth.
                </p>
              </motion.div>
            </div>
          </section>

          {/* ── CTA (ink panel) ── */}
          <section className="px-6 pb-24 pt-4">
            <motion.div
              {...inView}
              variants={fadeUp}
              className="blueprint-grid-dark relative mx-auto max-w-5xl overflow-hidden rounded-[28px] bg-charcoal px-8 py-16 text-center sm:py-20"
            >
              <div
                className="pointer-events-none absolute inset-0 opacity-40"
                style={{
                  background:
                    'radial-gradient(60% 120% at 15% 0%, rgba(58,104,160,0.45), transparent 60%), radial-gradient(60% 120% at 85% 100%, rgba(229,68,45,0.40), transparent 60%)',
                }}
              />
              <div className="relative">
                <h2 className="text-balance font-serif text-3xl leading-tight tracking-tight text-white sm:text-5xl">
                  Ready for better discourse?
                </h2>
                <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <button
                    onClick={() => navigate('/explore')}
                    className="w-full rounded-full bg-white px-8 py-3.5 text-base font-medium text-charcoal transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:bg-white/90 sm:w-auto"
                  >
                    Explore debates
                  </button>
                  <button
                    onClick={() => navigate(user ? '/arena' : '/signup')}
                    className="w-full rounded-full border border-white/25 px-8 py-3.5 text-base font-medium text-white transition-all duration-200 ease-out-expo hover:-translate-y-0.5 hover:bg-white/10 sm:w-auto"
                  >
                    Create an account
                  </button>
                </div>
                <a
                  href="https://x.com/steelman_ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-8 inline-flex items-center gap-2 font-mono text-xs text-white/50 transition-colors hover:text-white"
                >
                  <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                  </svg>
                  @steelman_ai
                </a>
              </div>
            </motion.div>
          </section>
        </div>
      </div>
    </MotionConfig>
  );
};

export default About;
